import { useMemo } from 'react'
import {
  describeTask,
  describeDependencies,
  holdingBlockers,
  blockerLabel,
} from '@trackmind/core'
import type { Task } from '@/types'
import { useApp } from '@/context/AppContext'
import { useToday } from '@/hooks/useToday'
import { InfoTip } from '@/components/ui/tooltip'

/**
 * Wrap anything in "what is this task, in words".
 *
 * A task row is a pictogram — a coloured dot, a flame, a lock, a clock — and
 * until now nothing in the app said what any of it meant. Hover (or, on a
 * phone, tap) any of the meta and this spells it out: which project, how
 * urgent, how hard, how long, how overdue, what it's waiting on, whether it's
 * even on the plan.
 *
 * The sentence itself is built in core so the wording is identical wherever a
 * task appears, and so it can be tested without a browser.
 *
 * The project name and the dependency titles come from context rather than
 * from props: this is a leaf, and threading two more props through nine
 * TaskCard call sites to feed a tooltip would be a poor trade.
 */
export function TaskInfoTip({
  task,
  scheduledFor,
  side = 'top',
  children,
}: {
  task: Task
  scheduledFor?: string | null
  side?: 'top' | 'right' | 'bottom' | 'left'
  children: React.ReactNode
}) {
  const { projects, tasks: allTasks, preferences } = useApp()
  const todayKey = useToday()

  const lines = useMemo(() => {
    const project = task.project_id
      ? projects.find((p) => p.id === task.project_id) ?? null
      : null
    // Only what's genuinely holding it back: a blocker that is paused,
    // archived or in a paused project is invisible to the scheduler, so
    // calling it a blocker here would contradict the plan.
    const blockedBy = holdingBlockers(describeDependencies(task, allTasks, projects, todayKey()))
      .map(blockerLabel)
    return describeTask(task, { project, blockedBy, scheduledFor, timezone: preferences?.timezone })
  }, [task, projects, allTasks, scheduledFor, preferences?.timezone, todayKey])

  return (
    <InfoTip
      side={side}
      label={
        <span className="block space-y-0.5">
          {lines.map((line, i) => (
            <span key={i} className={i === 0 ? 'block font-medium' : 'block text-muted-foreground'}>
              {line}
            </span>
          ))}
        </span>
      }
    >
      {children}
    </InfoTip>
  )
}
